import React, { useEffect, useMemo, useState } from 'react';
import FinancialTrendChart from '@components/charts/FinancialTrendChart';
import { mapFullYearResultsApiToPerformanceHistorySection } from '@apis/performanceTrendsMappers';
import {
  getApiParamErrorNotificationMessage,
  shouldNotifyDataRequestError,
} from '@apis/client';
import { useFullYearResults } from '@hooks/useFullYearResults';
import { notify } from '@utils/notify';
import TrendsSection from './TrendsSection';
import TrendsControls from './TrendsControls';
import FinancialMatrixTable from './FinancialMatrixTable';
import type { PerformanceHistorySection } from '@app-types/performanceTrends';
import type { FullYearForecastType } from '@app-types/performanceTrendsApi';

export interface AnnualPerformanceSectionProps {
  data: PerformanceHistorySection;
}

const isFullYearForecastType = (value: string): value is FullYearForecastType =>
  value === 'consensus' || value === 'company';

const initialForecastType = (value: string): FullYearForecastType =>
  isFullYearForecastType(value) ? value : 'consensus';

const toDisplayPeriodCount = (value: string): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 10;
};

/** Annual performance trend section (Figma 3131:2159). */
const AnnualPerformanceSection: React.FC<AnnualPerformanceSectionProps> = ({ data }) => {
  const [selectedForecast, setSelectedForecast] = useState<FullYearForecastType>(
    initialForecastType(data.controls.selectedForecast),
  );
  const [selectedPeriod, setSelectedPeriod] = useState(data.controls.selectedPeriod);
  const displayPeriodCount = toDisplayPeriodCount(selectedPeriod);
  const fullYearQuery = useFullYearResults(selectedForecast, displayPeriodCount);

  useEffect(() => {
    if (fullYearQuery.isError && shouldNotifyDataRequestError(fullYearQuery.error)) {
      notify({
        level: 'error',
        message: getApiParamErrorNotificationMessage(fullYearQuery.error) ??
          '通期業績推移の取得に失敗しました。',
      });
    }
  }, [fullYearQuery.error, fullYearQuery.isError]);

  const annualData = useMemo(
    () => mapFullYearResultsApiToPerformanceHistorySection(
      fullYearQuery.data,
      data,
      { forecastType: selectedForecast, displayPeriodCount },
    ),
    [fullYearQuery.data, data, selectedForecast, displayPeriodCount],
  );

  const handleForecastChange = (value: string): void => {
    if (isFullYearForecastType(value)) {
      setSelectedForecast(value);
    }
  };

  return (
    <TrendsSection
      title={annualData.title}
      headerNodeId={annualData.headerNodeId}
      sectionNodeId={annualData.sectionNodeId}
      anchorId="annual"
    >
      <TrendsControls
        data={annualData.controls}
        nodeId="3158:5179"
        selectedForecast={selectedForecast}
        selectedPeriod={selectedPeriod}
        onForecastChange={handleForecastChange}
        onPeriodChange={setSelectedPeriod}
      />
      <div className="trends-chart-grid">
        {annualData.charts.map((chart) => (
          <div key={chart.key} className="trends-history-card">
            <div className="trends-history-card__title">{chart.title}</div>
            <FinancialTrendChart data={chart} />
          </div>
        ))}
      </div>
      <FinancialMatrixTable data={annualData.table} caption={annualData.title} />
      <p className="trends-note">{annualData.note}</p>
    </TrendsSection>
  );
};

export default AnnualPerformanceSection;
